import { UserAssessmentWithDetailsResponse } from '@api/assessmentQuestionnaireApiHandler';
import leftArrow from '@assets/images/leftArow.svg';
import Img from '@components/Atoms/Img';
import { WingDetails } from '@components/Star';
import { useAppDispatch } from '@hooks/useAppDispatch';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { generateStar } from '@utils/star.util';
import dayjs from 'dayjs';
import EChartsReact from 'echarts-for-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  setChartSelectedAssessmentDetails,
  setLoadingScores,
} from 'store/Slices/AssessmentScoresSliceV2';
import styles from './index.module.scss';

const generalHappiness = '';

interface AreaProps {
  assessments: UserAssessmentWithDetailsResponse[];
  stars?: WingDetails[][];
}

interface RichDataObject {
  [key: string]: string | number | object;
}

export const AreaChart: React.FunctionComponent<AreaProps> = (props) => {
  const { assessments, stars = [] } = props;
  const dispatch = useAppDispatch();
  const { isDesktop } = useWindowDimensions();
  const ITEMS_PER_PAGE = isDesktop ? 10 : 4;
  const chartRef = useRef() as any;

  // Memoize sorted assessments with index mapping for performance
  const { sortedAssessments, indexMap } = useMemo(() => {
    const sorted = [...assessments].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    const map = new Map();
    sorted.forEach((assessment, index) => {
      map.set(assessment.id, index);
    });

    return { sortedAssessments: sorted, indexMap: map };
  }, [assessments]);

  const totalPages = Math.ceil(sortedAssessments.length / ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const prevPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const nextPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const paginatedAssessments = useMemo(() => {
    const pageData = sortedAssessments.slice(startIndex, endIndex);

    return pageData.reverse();
  }, [sortedAssessments, startIndex, endIndex]);

  // Optimized data processing using the index map
  const { modifiedValues, modifiedLabels } = useMemo(() => {
    const values = paginatedAssessments.map((assessment) => {
      const originalIndexV = indexMap.get(assessment.id) ?? -1;

      return {
        score: assessment.totalScore,
        assessmentId: assessment.id,
        date: dayjs(assessment.createdAt).format('DD/MM/YYYY'),
        created_at: assessment.createdAt,
        originalIndex: originalIndexV,
        questionnaireId: assessment?.questionnaireId || 0,
        questionnaireName: assessment?.questionnaireV2?.name || '',
      };
    });

    const labels = paginatedAssessments.map((assessment) =>
      dayjs(assessment.createdAt).format('DD MMM YY'),
    );

    // Handle single item case
    if (values.length === 1) {
      values.unshift({
        ...values[0],
        originalIndex: -1,
      });
      labels.unshift('');
    }

    return { modifiedValues: values, modifiedLabels: labels };
  }, [paginatedAssessments, indexMap]);

  // Memoize stars processing
  const processedStars = useMemo(() => {
    const maxValue = isDesktop ? 10 : 4;
    let modifiedStars = [...stars];

    if (stars.length < maxValue) {
      const emptyStars = new Array(maxValue - stars.length).fill([]);
      modifiedStars = [...emptyStars, ...stars];
    }

    modifiedStars.push([]);

    return modifiedStars;
  }, [stars, isDesktop]);

  // Memoize chart data
  const { data, labelData, series } = useMemo(() => {
    const chartData = modifiedValues.map((value, index) => ({
      score: Math.round(value.score * 100) / 100,
      assessmentId: value.assessmentId,
      date: value.date,
      index: value.originalIndex,
      localIndex: index,
      questionnaireId: value?.questionnaireId,
      questionnaireName: value?.questionnaireName,
    }));

    const labels = modifiedLabels;

    const seriesData = chartData.map((value, index) => {
      if (labels[index] === '') {
        return {
          value: value.score,
          assessmentId: value.assessmentId,
          index: value.index,
          localIndex: value.localIndex,
          date: value.date,
          label: { show: false },
          symbol: 'none',
          symbolSize: 0,
          questionnaireId: value?.questionnaireId,
          questionnaireName: value?.questionnaireName,
        };
      }

      if (value.index === selectedIndex) {
        return {
          value: value.score,
          assessmentId: value.assessmentId,
          index: value.index,
          localIndex: value.localIndex,
          date: value.date,
          symbolSize: 67,
          label: {
            fontSize: 20,
          },
          questionnaireId: value?.questionnaireId,
          questionnaireName: value?.questionnaireName,
        };
      }

      return {
        value: value.score,
        assessmentId: value.assessmentId,
        index: value.index,
        localIndex: value.localIndex,
        date: value.date,
        label: { show: true },
        questionnaireId: value?.questionnaireId,
        questionnaireName: value?.questionnaireName,
      };
    });

    return { data: chartData, labelData: labels, series: seriesData };
  }, [modifiedValues, modifiedLabels, selectedIndex]);

  // Memoize rich data
  const richData: RichDataObject = useMemo(() => {
    const baseRichData: RichDataObject = {
      value: {
        fontFamily: 'Titillium Web',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 30,
        align: 'center',
      },
      selected: {
        height: 4,
        backgroundColor: '#FCD300',
        width: 60,
        lineHeight: 15,
      },
    };

    // Only add star data if needed
    const hasNonEmptyLabels = labelData.some((item) => item !== '');
    if (hasNonEmptyLabels) {
      baseRichData[8] = {
        height: 0,
        backgroundColor: {
          image: generateStar(372, 428, [
            {
              color: '#019DF2',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#BEAAF3',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#FCD300',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#8EEA36',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#06BF97',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#FF8A4A',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
          ]),
        },
      };
    }

    return baseRichData;
  }, [labelData]);

  // Optimized click handler - removed redundant dispatch and timeout
  const handleDataPointClick = useCallback(
    (clickedItem: any) => {
      dispatch(setLoadingScores(true));

      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        dispatch(setLoadingScores(false));
      });

      const assessmentDetails = {
        assessmentId: clickedItem.assessmentId,
        date: clickedItem.date,
        index: clickedItem.index,
        score: clickedItem?.value || 0,
        questionnaireId: clickedItem?.questionnaireId || 0,
        questionnaireName: clickedItem?.questionnaireName,
      };

      dispatch(setChartSelectedAssessmentDetails(assessmentDetails));
      setSelectedIndex(clickedItem.index);
    },
    [dispatch],
  );

  // Fixed useEffect with proper cleanup and dependencies
  useEffect(() => {
    const instance = chartRef?.current?.getEchartsInstance();

    if (!instance) return;

    const handleClick = (params: any) => {
      const clickedItem = series[params?.dataIndex];
      if (clickedItem) {
        handleDataPointClick(clickedItem);
      }
    };

    // Remove existing listeners to prevent duplicates
    instance.off('click');
    instance.on('click', handleClick);

    // eslint-disable-next-line consistent-return
    return () => {
      if (instance) {
        instance.off('click', handleClick);
      }
    };
  }, [series, handleDataPointClick]);

  // Memoize chart options
  const option = useMemo(
    () => ({
      grid: { right: 40, left: 40 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        triggerEvent: true,
        data: labelData,
        axisPointer: {
          show: true,
          type: 'shadow',
          triggerEvent: false,
          trigger: 'item',
        },
        axisLabel: {
          margin: 30,
          align: 'center',
          rotate: 20,
          triggerEvent: true,
          trigger: 'axis',
          interval: 0,
          formatter: (value: string, index: number) => {
            const isCurrentItem = data[index]?.index === selectedIndex;
            const isLatestItem = data[index]?.index === 0;

            return [
              `{${value !== '' ? 8 : 0}|}`,
              `${isLatestItem ? 'Current' : value}`,
              isCurrentItem ? `{selected|}` : '',
            ].join('\n');
          },
          fontWeight: 'bold',
          rich: richData,
        },
      },
      yAxis: {
        type: 'value',
        interval: 10,
        max: 100,
        min: 0,
        triggerEvent: true,
        trigger: 'axis',
        axisLine: {
          show: true,
        },
        splitLine: {
          show: true,
        },
      },
      series: [
        {
          data: series,
          type: 'line',
          symbolSize: 44,
          smooth: true,
          label: {
            show: true,
            position: 'inside',
            triggerEvent: true,
            fontSize: 16,
            fontFamily: 'Titillium Web',
            color: '#505050',
          },
          itemStyle: {
            color: '#069B7B',
          },
          lineStyle: {
            color: '#06BF97',
          },
          areaStyle: {
            color: '#06BF97',
            opacity: 0.9,
          },
        },
      ],
    }),
    [labelData, data, selectedIndex, richData, series],
  );

  return (
    <div className={styles.hsAreaChart} style={{ maxWidth: '100%' }}>
      {/* Left arrow - shows older data - only show when there are previous pages available */}
      {currentPage < totalPages && (
        <div onClick={prevPage} style={{ cursor: 'pointer' }}>
          <Img src={leftArrow} alt="Previous Data" className={styles.arrowContLeft} />
        </div>
      )}
      <EChartsReact ref={chartRef} option={option} className={styles.eChart} />
      {/* Right arrow - shows newer data (back to latest) - only show when NOT on the latest page */}
      {currentPage > 1 && (
        <div onClick={nextPage} style={{ cursor: 'pointer' }}>
          <Img src={leftArrow} alt="Back to Latest" className={styles.arrowContRight} />
        </div>
      )}
    </div>
  );
};

AreaChart.defaultProps = {
  stars: [],
};
